import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import Text from '../../global/text/text';
import { Space } from '../../../constants/html-character-entities';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { alertServerError, showNotification } from '../../global/modal/alerts-host/alert-templates';
import { IAlertsService } from '../../../infrastructure/alerts';
import { UserAction } from '../../../api/users.dao';
import { TelephoneFormDataVM } from './telephone-form-data.viewmodel';
import { IZoomDAO, IZoomTelephoneNumberSetDTO } from '../../../api/zoom.dao';
import { IReactIntlProvider } from '../../../infrastructure/react-intl-provider';
import { NEW_NUMBER_SET_NOTIFICATION_TEXT, DEACTIVATE_NUMBER_SET_TEXT ,UPDATE_NUMBER_SET_NOTIFICATION_TEXT, ALERT_TEXT, ALERT_TEXT_DANGER, ALERT_TEXT_DESCRIPTION } from '../../../constants/telephone-number-set-form.text';
import { AlertErrorMessage } from '../../global/modal/alerts-host/types/alert/alert-error-message';
import React, { ChangeEvent } from 'react';
import { AudioBridgeProviderLabel } from '../../../infrastructure/utils/common.utils';
import { ERROR_TEXT } from '../../../constants/common-labels.constants';

class TelephoneFormVM {
    @mobx.observable
    public saving: boolean;

    @mobx.observable
    public telephoneFormDataVM: TelephoneFormDataVM;

    @mobx.observable
    public telephoneArchive: File;

    constructor(
        private fetchTelephoneNumberSets: () => void,
        private closeTelephoneNumberSetForm: (id: string) => void,
        public numberSet: IZoomTelephoneNumberSetDTO,
        @IoC.Inject private readonly telephoneNumberSetsDAO: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        this.telephoneFormDataVM = new TelephoneFormDataVM();
        this.telephoneFormDataVM.telephoneArchiveName = (this.getProviderName(numberSet?.provider) ?? '') + '_' + (numberSet?.name ?? '');
        this.telephoneFormDataVM.initialize(numberSet);
    }

    public get intl(): IntlShape { return this.intlProvider.getIntl(); }

    public isSaving(): boolean {
        return this.saving;
    }

    @mobx.action
    public setThemeArchive = (e: ChangeEvent<HTMLInputElement>) => {
        this.telephoneArchive = e.currentTarget.files[0];
    };

    public getFormDataVM(): TelephoneFormDataVM {
        return this.telephoneFormDataVM;
    }

    public downloadNumberSet = async (): Promise<void> => {
        await this.telephoneNumberSetsDAO.downloadTelephoneNumberSet({audioProviderId: this.telephoneFormDataVM.audioProviderId});
    };

    public inactivateNumberSet = () : void => {
        this.alertsService.confirm(
            {
                message: (
                    <>
                        <Text
                            className='alert-message'
                            {...ALERT_TEXT}
                        />
                        <Space />
                        <Text
                            className='alert-message--danger'
                            {...ALERT_TEXT_DANGER}
                        />?
                        <br />
                        <Text
                            className='alert-message'
                            {...ALERT_TEXT_DESCRIPTION}
                        />?

                    </>
                ),
                confirmButtonProps: {
                    text: this.intl.formatMessage(DEACTIVATE_NUMBER_SET_TEXT),
                    modifier: 'confirm-warn',
                    handler: this.deactivateNumberSets,
                },
                cancelButtonProps: {
                    modifier: 'cancel'
                }
            }
        );
    };

    private deactivateNumberSets = async (): Promise<void> => {
        try {
            const id = this.telephoneFormDataVM.audioProviderId;
            await this.telephoneNumberSetsDAO.deactivateTelephoneNumberSet({audioProviderId: id});
            this.closeTelephoneNumberSetForm(id);
            this.fetchTelephoneNumberSets();
        } catch (error) {
            alertServerError(this.intl.formatMessage(ERROR_TEXT), this.errorParsingService.extractMessage(error));
        }
    };

    public getErrorMessage(): string | null {
        const { showValidationErrors, getNameError, serverErrorMessage } = this.telephoneFormDataVM;
        if (showValidationErrors() && getNameError()) {
            return getNameError();
        }
        return serverErrorMessage;
    }

    @mobx.action
    public handleSubmit = async (userAction: string): Promise<boolean> => {
        this.getFormDataVM().serverErrorMessage = '';
        const isFormInvalid = !this.getFormDataVM().isFormValid();
        if (isFormInvalid) {
            this.getFormDataVM().allowToShowValidationErrors();
            return false;
        }
        try {
            this.saving = true;
            const telephoneArchive = this.telephoneFormDataVM.telephoneArchive;
            let numberSetData;
            if (userAction === UserAction.Create) {
                numberSetData = this.telephoneFormDataVM.toApiCreateNumberSetDTO();
                await this.telephoneNumberSetsDAO.createTelephoneNumberSet(numberSetData);
                showNotification(this.intl.formatMessage(NEW_NUMBER_SET_NOTIFICATION_TEXT));
            }
            if (userAction === UserAction.Edit) {
                numberSetData = this.telephoneFormDataVM.toApiUpdateNumberSetDTO(telephoneArchive);
                await this.telephoneNumberSetsDAO.updateTelephoneNumberSet(numberSetData);
                showNotification(this.intl.formatMessage(UPDATE_NUMBER_SET_NOTIFICATION_TEXT));
            }
            this.fetchTelephoneNumberSets();
            return true;
        } catch (error) {
            this.onCreateNumberSetError(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    @mobx.action
    private onCreateNumberSetError(error): void {
        this.alertsService.alert({
            message: (
                <AlertErrorMessage title='Error' message={this.errorParsingService.extractMessage(error)} />
            ),
            renderCrossIcon: false,
            okButtonProps: {
                text: 'Ok',
            },
        });
    }

    @mobx.action
    public getProviderName(provider:string): string| null {
        return AudioBridgeProviderLabel[provider];
    }
}

export { TelephoneFormVM };
